import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Checkbox, Stack } from '@lyearn/ui';

import { useTaskBulkSelect } from '../../../../contexts/TaskBulkSelectContext';

type Props = {
  className?: string;
  groupByKey?: string;
  groupHoverClassName?: string;
};

const GroupHeaderCheckbox = ({ className, groupHoverClassName = '', groupByKey }: Props) => {
  const { t } = useTranslation();
  const {
    selectedGroups,
    setSelectedGroups,
    setExcludeActionItemIds,
    setIncludeActionItemIds,
    excludeActionItemIds,
    includeActionItemIds,
    groupedActionItems,
  } = useTaskBulkSelect();

  const checked = selectedGroups.includes(groupByKey ?? '');

  const actionItemIdsForCurrentGroupKey = groupedActionItems
    .find((group) => group.groupByKey === groupByKey)
    ?.actionItems.map((actionItem) => actionItem.id);

  const groupHasSomeItemsSelected =
    !checked && includeActionItemIds?.some((id) => actionItemIdsForCurrentGroupKey?.includes(id));
  const groupHasSomeItemsDeselected =
    checked && excludeActionItemIds?.some((id) => actionItemIdsForCurrentGroupKey?.includes(id));
  const indeterminate = groupHasSomeItemsSelected || groupHasSomeItemsDeselected;

  const onChange = useCallback(() => {
    if (checked) {
      setSelectedGroups((prev) => prev.filter((group) => group !== groupByKey));
    } else {
      setSelectedGroups((prev) => [...prev, groupByKey ?? '']);
    }
    setExcludeActionItemIds((prev) =>
      prev.filter((id) => !actionItemIdsForCurrentGroupKey?.includes(id)),
    );
    setIncludeActionItemIds((prev) =>
      prev.filter((id) => !actionItemIdsForCurrentGroupKey?.includes(id)),
    );
  }, [
    actionItemIdsForCurrentGroupKey,
    checked,
    groupByKey,
    setExcludeActionItemIds,
    setIncludeActionItemIds,
    setSelectedGroups,
  ]);

  return (
    <Stack
      className={cn(className, { [groupHoverClassName]: !checked && !indeterminate })}
      onClick={(e) => e.stopPropagation()}>
      {groupByKey ? (
        <Checkbox
          checked={checked}
          indeterminate={indeterminate}
          size="small"
          onChange={onChange}
        />
      ) : (
        <Box className="w-18 h-18 shrink-0" />
      )}
    </Stack>
  );
};

export default React.memo(GroupHeaderCheckbox);
